import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { uploadDocs, uploadQuery } from '../api/devices'
import { FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Upload documents - inventoree/devices/others/upload.php si upload-1.js:
 * aceleasi trei chei de 4 MB, confirm, toastr Documents uploaded.
 *
 * CELE 4 MB SE SI MASOARA. In macheta veche "max file size 4MB." era un rand
 * de text sub casuta si nimic altceva: fisierul pleca oricum, iar cine il
 * refuza era capatul de upload, mut. Aici marimea se verifica de doua ori - o
 * data aici, ca omul sa afle pe loc, si o data pe server
 * (`DeviceWrites::MAX_DOCUMENT_BYTES`), fiindca ce spune pagina nu e o paza.
 *
 * Si raspunsul se citeste. `ajaxForm` din portal arata "Documents uploaded"
 * chiar si cand nu urcase nimic; un 413 / 502 / 503 ajunge acum in toast, cu
 * motivul lui.
 *
 * ASEZAREA e cea comuna (`ui/FormGrid.tsx`), cu O ABATERE SCRISA DINADINS:
 * casuta de fisier NU primeste `form-control`. Clasa aia da inaltime fixa si
 * indesare, iar butonul nativ ("Choose file") e desenat de browser INAUNTRUL
 * campului - deci se taie. Casuta sta pe coloana grilei, cu eticheta deasupra
 * si cu "max file size 4MB." dedesubt, ca lamurirea oricarui alt camp, dar
 * ramane cat ii trebuie. Acelasi lucru scrie si in capul lui `FormGrid`.
 *
 * Inainte, eticheta statea la stanga, pe trei coloane din douasprezece, intr-un
 * `<span class="color">` - pastila gri de 25px pe care numai `od_console.css` o
 * stinge, si aia e aprinsa doar in Settings.
 */

/** "max file size 4MB." - acelasi prag ca pe server. */
const MAX_BYTES = 4 * 1024 * 1024

const KEYS = [
  { name: 'warranty_doc', label: 'Warranty document' },
  { name: 'extra_warranty_doc', label: 'Extended Warranty document' },
  { name: 'warranty_bill_doc', label: 'Warranty bill document' },
] as const

type Key = (typeof KEYS)[number]['name']

export function DeviceUpload({ id }: ScreenProps) {
  const { data, error } = useQuery(uploadQuery(id))
  const [files, setFiles] = useState<Partial<Record<Key, File>>>({})
  const [busy, setBusy] = useState(false)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const submit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    const form = ev.currentTarget
    const chosen = KEYS.map((k) => [k.name, files[k.name]] as const).filter(
      (pair): pair is readonly [Key, File] => pair[1] !== undefined,
    )
    if (chosen.length === 0) {
      toast('Choose a document first.', 'Error', 'error')
      return
    }
    const big = chosen.find(([, file]) => file.size > MAX_BYTES)
    if (big) {
      toast(`${big[1].name} is over the 4MB limit.`, 'Error', 'error')
      return
    }
    if (!confirm('Are you sure?')) return
    const body = new FormData()
    body.set('device', String(data.device.id))
    for (const [key, file] of chosen) body.set(key, file)
    setBusy(true)
    try {
      await uploadDocs(data.device.id, body)
      toast('Documents uploaded', 'Success', 'success')
      form.reset()
      setFiles({})
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Error', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="device-upload">
      <div className="notifications-box"></div>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Upload</strong> documents
        </h4>
      </header>
      <div className="panel-body">
        <form className="uploadDeviceDocs" encType="multipart/form-data" method="post" onSubmit={submit}>
          <input type="hidden" name="device" value={data.device.id} />
          {KEYS.map((key) => (
            <FormRow key={key.name}>
              <FileField
                label={key.label}
                name={key.name}
                onFile={(file) =>
                  setFiles((prev) => {
                    const next = { ...prev }
                    if (file) next[key.name] = file
                    else delete next[key.name]
                    return next
                  })
                }
              />
            </FormRow>
          ))}
          <FormActions>
            <button type="submit" className="btn btn-theme" disabled={busy}>
              Upload
            </button>
          </FormActions>
        </form>
      </div>
    </div>
  )
}

function FileField({ label, name, onFile }: { label: string; name: string; onFile(file: File | null): void }) {
  const [tooBig, setTooBig] = useState(false)
  return (
    <HalfField
      label={label}
      htmlFor={name}
      help={<span className={tooBig ? 'text-danger' : undefined}>max file size 4MB.</span>}
    >
      <input
        type="file"
        id={name}
        name={name}
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null
          setTooBig(file !== null && file.size > MAX_BYTES)
          onFile(file)
        }}
      />
    </HalfField>
  )
}
